import React from 'react';
import { motion } from 'framer-motion';

const RetailTherapyVsAddictionLanding = ({ goToInput, theme = "mint" }) => {
  // Theme-based button styling
  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl p-6 mb-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* SEO-optimized heading structure */}
      <h1 className="text-3xl font-bold text-center mb-6 dark:text-white">
        Retail Therapy vs. Shopping Addiction: Where's The Line?
      </h1>
      
      {/* Hero section with main CTA */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-xl p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center">
          <div className="mb-6 md:mb-0 md:mr-6 text-center md:text-left">
            <h2 className="text-2xl font-bold mb-3 dark:text-white">Is Your Shopping Helping or Hurting?</h2>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              Discover where healthy retail therapy ends and problematic shopping addiction begins. Track your habits and get personalized insights.
            </p>
            <motion.button
              onClick={goToInput}
              className={`px-6 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Track Your Shopping Habits
            </motion.button>
          </div>
          <div className="text-8xl flex items-center">
            <span className="mr-2">🛍️</span>
            <span className="text-4xl">vs</span>
            <span className="ml-2">⚠️</span>
          </div>
        </div>
      </div>
      
      {/* Comparison Chart Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Retail Therapy vs. Shopping Addiction: Key Differences</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="p-3 text-left border-b border-gray-200 dark:border-gray-600 w-1/3">Behavior</th>
                <th className="p-3 text-left border-b border-gray-200 dark:border-gray-600 w-1/3 text-emerald-600 dark:text-emerald-400">Retail Therapy (Healthy)</th>
                <th className="p-3 text-left border-b border-gray-200 dark:border-gray-600 w-1/3 text-red-600 dark:text-red-400">Shopping Addiction (Problematic)</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <td className="p-3 font-medium dark:text-white">Frequency</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Occasional shopping to boost mood</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Constant urges and frequent shopping despite consequences</td>
              </tr>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <td className="p-3 font-medium dark:text-white">Financial Impact</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Within budget, planned</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Often leads to debt, financial strain</td>
              </tr>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <td className="p-3 font-medium dark:text-white">Emotional State</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Temporary mood boost with lasting satisfaction</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Brief high followed by guilt, shame, anxiety</td>
              </tr>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <td className="p-3 font-medium dark:text-white">Control</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Can easily stop or delay purchases</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Failed attempts to cut back, feels compulsive</td>
              </tr>
              <tr className="border-b border-gray-200 dark:border-gray-600">
                <td className="p-3 font-medium dark:text-white">Social Impact</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Little to no effect on relationships</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">May hide purchases, lie about spending, avoid social activities</td>
              </tr>
              <tr>
                <td className="p-3 font-medium dark:text-white">After-Effects</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Genuine enjoyment of purchases</td>
                <td className="p-3 text-gray-600 dark:text-gray-300">Many items unused, forgotten, or stockpiled</td>
              </tr>
            </tbody>
          </table>
        </div>
      </motion.section>
      
      {/* Warning Signs Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">10 Warning Signs Your Shopping May Be Problematic</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">1️⃣</span>
              <div>
                <h3 className="font-bold dark:text-white">Shopping When Upset</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Consistently shopping is your primary coping mechanism when feeling negative emotions.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">2️⃣</span>
              <div>
                <h3 className="font-bold dark:text-white">Financial Struggles</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Shopping has caused credit card debt or inability to pay important bills.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">3️⃣</span>
              <div>
                <h3 className="font-bold dark:text-white">Hiding Purchases</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Concealing shopping bags, lying about prices, or having parcels delivered secretly.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">4️⃣</span>
              <div>
                <h3 className="font-bold dark:text-white">Unopened Items</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Having many unused purchases with tags still attached or unopened packages.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">5️⃣</span>
              <div>
                <h3 className="font-bold dark:text-white">Shopping Rituals</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Feeling an intense rush when shopping that you can't get from other activities.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">6️⃣</span>
              <div>
                <h3 className="font-bold dark:text-white">Relationship Problems</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Shopping has caused arguments or tension with loved ones multiple times.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">7️⃣</span>
              <div>
                <h3 className="font-bold dark:text-white">Failed Attempts to Stop</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  You've tried to cut back on shopping multiple times without success.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">8️⃣</span>
              <div>
                <h3 className="font-bold dark:text-white">Shopping Anxiety</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Feeling restless, irritable, or anxious when unable to shop.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">9️⃣</span>
              <div>
                <h3 className="font-bold dark:text-white">Loss of Interest</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Giving up activities you once enjoyed to have more time or money for shopping.
                </p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex items-start">
              <span className="text-2xl mr-3">🔟</span>
              <div>
                <h3 className="font-bold dark:text-white">Preoccupation</h3>
                <p className="text-gray-600 dark:text-gray-300 text-sm">
                  Spending excessive time thinking about shopping, browsing online stores, or planning purchases.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Shopping Addiction Stats */}
      <motion.section 
        className="mb-8 bg-gray-50 dark:bg-gray-700 rounded-xl p-5"
        variants={itemVariants}
      >
        <h2 className="text-xl font-bold mb-4 dark:text-white">Shopping Addiction By The Numbers</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold dark:text-white">5-8%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of Americans have compulsive buying disorder</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">80%</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">of compulsive buyers are women</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">$5,400</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">average annual debt from compulsive shopping</p>
          </div>
          
          <div>
            <div className="text-2xl font-bold dark:text-white">20s</div>
            <p className="text-sm text-gray-600 dark:text-gray-400">typical age when shopping addiction begins</p>
          </div>
        </div>
      </motion.section>
      
      {/* Healthy vs Unhealthy Shopping Quiz */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Quick Self-Assessment Quiz</h2>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
          <p className="mb-3 text-gray-700 dark:text-gray-300">Answer these questions honestly to get a quick idea of your shopping patterns:</p>
          
          <div className="space-y-4">
            <div>
              <p className="font-medium mb-2 dark:text-white">1. Do you shop when feeling stressed, sad, or anxious?</p>
              <div className="flex flex-wrap gap-2">
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Rarely</button>
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Sometimes</button>
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Often</button>
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Always</button>
              </div>
            </div>
            
            <div>
              <p className="font-medium mb-2 dark:text-white">2. Have you tried to reduce your shopping but couldn't?</p>
              <div className="flex flex-wrap gap-2">
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Never tried</button>
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Successfully reduced</button>
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Tried without success</button>
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Multiple failed attempts</button>
              </div>
            </div>
            
            <div>
              <p className="font-medium mb-2 dark:text-white">3. Do you hide purchases or receipts from others?</p>
              <div className="flex flex-wrap gap-2">
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Never</button>
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Rarely</button>
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Sometimes</button>
                <button className="px-3 py-1 rounded-full bg-gray-200 dark:bg-gray-600 text-sm">Frequently</button>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <motion.button
                className={`px-6 py-2 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={goToInput}
              >
                Take The Full Assessment
              </motion.button>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Our full assessment provides detailed insights and personalized recommendations
              </p>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* The Spectrum Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">The Shopping Behavior Spectrum</h2>
        
        <div className="bg-gradient-to-r from-green-50 via-yellow-50 to-red-50 dark:from-green-900/20 dark:via-yellow-900/20 dark:to-red-900/20 rounded-xl p-5">
          <div className="mb-4">
            <div className="relative h-4 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div className="absolute top-0 left-0 h-full w-full bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded-full"></div>
            </div>
            <div className="flex justify-between text-xs mt-1">
              <span className="text-green-600 dark:text-green-400">Healthy Shopping</span>
              <span className="text-yellow-600 dark:text-yellow-400">Occasional Retail Therapy</span>
              <span className="text-red-600 dark:text-red-400">Shopping Addiction</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-l-4 border-green-500">
              <h3 className="font-bold text-green-600 dark:text-green-400 mb-2">Healthy Shopping</h3>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                <li>• Budget-conscious purchases</li>
                <li>• Shopping with purpose</li>
                <li>• Considers need vs. want</li>
                <li>• Researches before buying</li>
                <li>• No emotional distress</li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-l-4 border-yellow-500">
              <h3 className="font-bold text-yellow-600 dark:text-yellow-400 mb-2">Occasional Retail Therapy</h3>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                <li>• Occasional mood-based shopping</li>
                <li>• Small splurges within means</li>
                <li>• Temporary mood improvement</li>
                <li>• No significant consequences</li>
                <li>• Can easily stop when needed</li>
              </ul>
            </div>
            
            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border-l-4 border-red-500">
              <h3 className="font-bold text-red-600 dark:text-red-400 mb-2">Shopping Addiction</h3>
              <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                <li>• Compulsive, uncontrollable urges</li>
                <li>• Financial consequences</li>
                <li>• Guilt, shame, anxiety after shopping</li>
                <li>• Concealing behavior</li>
                <li>• Interference with daily life</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* Recovery Strategies */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Healing From Shopping Addiction</h2>
        
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">1</span>
              Track Your Shopping Patterns
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Use our tracking tool to identify triggers, emotional states, and spending patterns that contribute to your shopping behavior.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">2</span>
              Create Financial Boundaries
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Set shopping budgets, use cash-only approaches, delete shopping apps, and consider freezing credit cards temporarily.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">3</span>
              Develop Alternative Coping Skills
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Find healthier ways to cope with emotions: exercise, creative hobbies, nature walks, meditation, or connecting with friends.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">4</span>
              Seek Professional Support
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Consider therapy with professionals specializing in compulsive behaviors, addiction, or financial therapy. Support groups can also help.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold flex items-center dark:text-white">
              <span className="bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 h-6 w-6 rounded-full flex items-center justify-center mr-2">5</span>
              Practice Mindful Shopping
            </h3>
            <p className="text-gray-600 dark:text-gray-300 ml-8">
              Implement a 24-hour waiting period for purchases, create shopping lists, and ask "Do I need this?" before buying anything.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* Testimonials section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Recovery Stories</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I never realized my 'retail therapy' had crossed the line until I found myself hiding purchases and lying about money. Tracking my shopping triggers helped me see the pattern and develop healthier coping mechanisms."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Morgan T.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">2 Years in Recovery</p>
              </div>
            </div>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <div className="flex flex-col h-full">
              <p className="text-gray-600 dark:text-gray-300 italic mb-4">
                "I thought everyone shopped when stressed, but when I calculated how much debt my habit created, I was shocked. Using the budget tools and mindfulness techniques has completely transformed my relationship with shopping."
              </p>
              <div className="mt-auto">
                <p className="font-semibold dark:text-white">Jamie L.</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">18 Months Shopping-Sober</p>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
      
      {/* FAQ Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Common Questions About Shopping Addiction</h2>
        <div className="space-y-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is retail therapy always harmful?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              No, occasional retail therapy within your budget can be a harmless mood booster. The problem occurs when shopping becomes your primary coping mechanism, causes financial strain, or leads to feelings of guilt and shame. Our tool helps you identify if your retail therapy has crossed into problematic territory.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Is shopping addiction a real mental health condition?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Yes, compulsive buying disorder (shopping addiction) is recognized by mental health professionals as a behavioral addiction with patterns similar to other addictive behaviors. While not officially in the DSM-5, research supports its classification as a legitimate condition that can significantly impact one's financial health and wellbeing.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">Can I recover from shopping addiction on my own?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Many people successfully address mild to moderate compulsive shopping through self-help strategies like spending tracking, trigger identification, and behavioral changes. However, if your addiction is severe or causing significant problems, professional help from a therapist specializing in addiction or behavioral disorders is recommended.
            </p>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold dark:text-white">How can I help someone with shopping addiction?</h3>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              Approach them with compassion rather than judgment, suggest tools like our shopping tracker, offer to be an accountability partner, encourage professional help if needed, avoid enabling behaviors, and educate yourself about compulsive buying disorder. Focus on the person, not just their shopping habits.
            </p>
          </div>
        </div>
      </motion.section>
      
      {/* CTA section */}
      <motion.section 
        className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/30 dark:to-purple-900/30 rounded-xl p-6 text-center"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-3 dark:text-white">Find Your Place on the Shopping Spectrum</h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          Track your shopping habits today and get personalized insights on whether your retail therapy is healthy or problematic.
        </p>
        <motion.button
          onClick={goToInput}
          className={`px-8 py-3 rounded-xl text-white font-semibold ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Start Tracking Your Shopping
        </motion.button>
      </motion.section>
      
      {/* Expert Advice Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Expert Advice: Finding Balance</h2>
        
        <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-5">
          <div className="flex items-start mb-4">
            <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-2xl mr-4 flex-shrink-0">
              👩‍⚕️
            </div>
            <div>
              <h3 className="font-bold dark:text-white">Dr. Sarah Martin</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Clinical Psychologist specializing in behavioral addictions</p>
            </div>
          </div>
          
          <blockquote className="italic text-gray-600 dark:text-gray-300 border-l-4 border-blue-300 dark:border-blue-700 pl-4 mb-4">
            "The line between healthy retail therapy and shopping addiction isn't about how much you spend, but rather the emotional context and consequences of your shopping. Ask yourself: Am I in control of my shopping, or is my shopping controlling me?"
          </blockquote>
          
          <p className="text-gray-600 dark:text-gray-300 mb-3">
            Dr. Martin recommends these three questions to assess your relationship with shopping:
          </p>
          
          <div className="space-y-2 mb-4">
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
              <p className="font-medium dark:text-white">1. Does shopping provide temporary relief but long-term problems?</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
              <p className="font-medium dark:text-white">2. Do you continue shopping despite negative consequences?</p>
            </div>
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg">
              <p className="font-medium dark:text-white">3. Do you feel a loss of control around shopping behavior?</p>
            </div>
          </div>
          
          <p className="text-gray-600 dark:text-gray-300">
            If you answered yes to any of these questions, it may be worth exploring your shopping habits more deeply with our tracking tool or speaking with a mental health professional.
          </p>
        </div>
      </motion.section>
      
      {/* Resources Section */}
      <motion.section 
        className="mb-8"
        variants={itemVariants}
      >
        <h2 className="text-2xl font-bold mb-4 dark:text-white">Additional Resources</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold mb-2 dark:text-white">Support Organizations</h3>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> 
                <div>
                  <span className="font-medium">Debtors Anonymous:</span> Support groups specifically for those struggling with compulsive spending
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> 
                <div>
                  <span className="font-medium">SAMHSA Helpline:</span> 1-800-662-HELP (4357) - Free, confidential treatment referral
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> 
                <div>
                  <span className="font-medium">National Foundation for Credit Counseling:</span> Financial counseling for debt management
                </div>
              </li>
            </ul>
          </div>
          
          <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4">
            <h3 className="font-bold mb-2 dark:text-white">Recommended Reading</h3>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> 
                <div>
                  <span className="font-medium">To Buy or Not to Buy by April Benson</span> - Guide to breaking shopping addiction
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> 
                <div>
                  <span className="font-medium">Shiny Objects by James A. Roberts</span> - America's obsession with buying stuff
                </div>
              </li>
              <li className="flex items-start">
                <span className="text-sm mr-2">•</span> 
                <div>
                  <span className="font-medium">Spent by Sally Palaian</span> - Recovery program for spending addiction
                </div>
              </li>
            </ul>
          </div>
        </div>
      </motion.section>
      
      {/* Rich SEO footer content */}
      <footer className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-gray-200">Understanding Retail Therapy vs. Shopping Addiction</h2>
          <p className="mb-3">
            The difference between harmless retail therapy and problematic shopping addiction lies in frequency, control, emotional impact, and consequences. While occasional mood-based shopping can be a harmless way to boost your spirits, shopping addiction involves compulsive behavior that causes financial strain, emotional distress, and interpersonal problems.
          </p>
          <p className="mb-3">
            Our Shopping Habit Tracker helps you understand where you fall on this spectrum by analyzing your shopping triggers, emotional patterns, and the impact of purchases on your life. By identifying early warning signs, you can develop healthier relationships with shopping and find more effective ways to manage stress and emotions.
          </p>
          <p>
            Whether you're concerned about your own shopping habits or those of a loved one, our free tool provides valuable insights and personalized recommendations to help navigate the complex territory between healthy retail therapy and problematic shopping addiction.
          </p>
        </div>
      </footer>
    </motion.div>
  );
};

export default RetailTherapyVsAddictionLanding;
